import type {
  RunnerDecorSprite,
  RunnerLevel,
  RunnerPathPingPongMotion,
  RunnerPathPingPongPhase,
} from '../../domain/runner/runnerTypes';

function opacityForMoveProgress(t: number): number {
  if (t <= 0 || t >= 1) {
    return 0;
  }
  return Math.sin(Math.PI * t);
}

function poseAlongLeg(
  motion: RunnerPathPingPongMotion,
  fromStart: boolean,
  t: number,
): Pick<RunnerDecorSprite, 'centerS' | 'centerLane' | 'opacity'> {
  const clamped = Math.min(1, Math.max(0, t));
  const aS = fromStart ? motion.startS : motion.finishS;
  const aLane = fromStart ? motion.startLane : motion.finishLane;
  const bS = fromStart ? motion.finishS : motion.startS;
  const bLane = fromStart ? motion.finishLane : motion.startLane;
  return {
    centerS: aS + (bS - aS) * clamped,
    centerLane: aLane + (bLane - aLane) * clamped,
    opacity: opacityForMoveProgress(clamped),
  };
}

function endpointPose(
  motion: RunnerPathPingPongMotion,
  atFinish: boolean,
): Pick<RunnerDecorSprite, 'centerS' | 'centerLane' | 'opacity'> {
  return {
    centerS: atFinish ? motion.finishS : motion.startS,
    centerLane: atFinish ? motion.finishLane : motion.startLane,
    opacity: 0,
  };
}

function advancePhase(
  phase: RunnerPathPingPongPhase,
): RunnerPathPingPongPhase {
  switch (phase) {
    case 'toFinish':
      return 'dwellFinish';
    case 'dwellFinish':
      return 'toStart';
    case 'toStart':
      return 'dwellStart';
    case 'dwellStart':
      return 'toFinish';
  }
}

function phaseDuration(motion: RunnerPathPingPongMotion): number {
  if (motion.phase === 'toFinish' || motion.phase === 'toStart') {
    return motion.durationSec;
  }
  return motion.dwellSec;
}

function tickPathPingPongDecor(
  decor: RunnerDecorSprite,
  deltaSeconds: number,
  motionScale: number,
): RunnerDecorSprite {
  const motion = decor.motion;
  if (motion == null || motion.kind !== 'pathPingPong') {
    return decor;
  }
  if (motionScale <= 0 || deltaSeconds <= 0) {
    return decor;
  }

  let phase = motion.phase;
  let elapsedSec = motion.elapsedSec + deltaSeconds * motionScale;
  let pose: Pick<RunnerDecorSprite, 'centerS' | 'centerLane' | 'opacity'>;

  while (true) {
    if (elapsedSec < 0) {
      pose = endpointPose(motion, false);
      break;
    }

    const duration = phaseDuration({ ...motion, phase });
    if (duration <= 0 || elapsedSec >= duration) {
      elapsedSec -= Math.max(duration, 0);
      phase = advancePhase(phase);
      continue;
    }

    if (phase === 'toFinish') {
      const t = duration <= 0 ? 1 : elapsedSec / duration;
      pose = poseAlongLeg(motion, true, t);
    } else if (phase === 'toStart') {
      const t = duration <= 0 ? 1 : elapsedSec / duration;
      pose = poseAlongLeg(motion, false, t);
    } else if (phase === 'dwellFinish') {
      pose = endpointPose(motion, true);
    } else {
      pose = endpointPose(motion, false);
    }
    break;
  }

  return {
    ...decor,
    ...pose,
    motion: {
      ...motion,
      phase,
      elapsedSec,
    },
  };
}

/** Ping-pong mirage-декоры; тот же level, если ничего не изменилось. */
export function tickRunnerPathDecorsUseCase(
  level: RunnerLevel,
  deltaSeconds: number,
  motionScale = 1,
): RunnerLevel {
  let changed = false;
  const decors = level.decors.map((decor) => {
    if (decor.motion?.kind !== 'pathPingPong') {
      return decor;
    }
    const next = tickPathPingPongDecor(decor, deltaSeconds, motionScale);
    if (next !== decor) {
      changed = true;
    }
    return next;
  });
  if (!changed) {
    return level;
  }
  return { ...level, decors };
}
